import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import {
  type TrackSelectionTrigger,
  useTrackDeselectedItems,
  useTrackDisabledSelectAll,
  useTrackSelectedItems,
} from '@air/analytics';
import {
  itemIsSelectedSelector,
  resetSelectedItemsAction,
  selectedItemsSelector,
  unselectItemAction,
} from '@air/redux-selected-items';

import { calculateRankForSingleSelection } from '~/analytics/rankUtils';
import { getTrackSelectedItemsCountsAndType } from '~/analytics/utils';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { selectGalleryItemAction } from '~/store/selectedItems/actions';
import { type SelectableGalleryItem } from '~/store/selectedItems/types';
import { useAirStore } from '~/utils/ReduxUtils';

export function useToggleSelectedItem() {
  const dispatch = useDispatch();
  const store = useAirStore();
  const { trackDeselectedItems } = useTrackDeselectedItems();
  const { trackDisabledSelectAll } = useTrackDisabledSelectAll();
  const { trackSelectedItems } = useTrackSelectedItems();
  const selectAll = useViewsSelectAllStore((state) => state.selectAll);
  const resetSelectAll = useViewsSelectAllStore((state) => state.resetSelectAll);

  const toggleSelectedItem = useCallback(
    ({
      item,
      trigger,
      forceToggle,
    }: {
      item: SelectableGalleryItem;
      trigger: TrackSelectionTrigger;
      forceToggle?: 'select' | 'unselect';
    }) => {
      const itemIsSelected = itemIsSelectedSelector(store.getState(), item.id);
      const selectedItems = selectedItemsSelector(store.getState());
      if (itemIsSelected || (forceToggle && forceToggle !== 'select')) {
        if (selectAll) {
          trackDisabledSelectAll({ trigger: 'item-click', totalSelectionCount: selectedItems.length });
          resetSelectAll();
          dispatch(resetSelectedItemsAction());
          return;
        }
        const nextSelectedItems = selectedItems.filter((i) => i.id !== item.id);
        const trackingParams = getTrackSelectedItemsCountsAndType(nextSelectedItems);
        trackDeselectedItems({ ...trackingParams, trigger });
        dispatch(unselectItemAction({ itemId: item.id }));
      } else {
        const nextSelectedItems = [...selectedItems, item];
        const trackingParams = getTrackSelectedItemsCountsAndType(nextSelectedItems);
        const rank = calculateRankForSingleSelection(store, nextSelectedItems);

        trackSelectedItems({
          ...trackingParams,
          trigger,
          ...(rank !== undefined && { rank }),
        });
        dispatch(selectGalleryItemAction({ item }));
      }
    },
    [dispatch, store, trackDeselectedItems, trackDisabledSelectAll, trackSelectedItems, selectAll, resetSelectAll],
  );

  return { toggleSelectedItem };
}
